import apiClient from "@/APIClient";
import { useToast, type ToastOptions } from "@/composable/useToast";
import { Logger } from "@/logger";
import { useTranslation } from "i18next-vue";
import { ref } from "vue";
import JSZip from "jszip";
import { Facade } from "@/drawing";

/** Téléchargement / export : toasts conservés même dans l'outil dessin. */
const ALLOW_ON_CANVAS: ToastOptions = { allowOnCanvas: true };

export const useDownloadAllBranches = () => {
  const { showLoading, showDanger, showSuccess } = useToast();
  const { t } = useTranslation();
  const isLoading = ref(false);

  async function mutateAsync(fileName: string, drawingId: number) {
    let loadingToastId = showLoading(
      t("dashboard:drawings.download.toast.converting"),
      undefined,
      ALLOW_ON_CANVAS
    );
    isLoading.value = true;
    try {
      const branches = await apiClient.fetchDrawingBranches(drawingId);
      const activeBranches = branches.filter((b) => b.headRevisionId != null);

      loadingToastId.close();
      loadingToastId = showLoading(
        t("dashboard:drawings.download.toast.downloadingDwg"),
        undefined,
        ALLOW_ON_CANVAS
      );

      const dwgEntries: { fileNameWithExtension: string; blob: Blob }[] = [];
      await Promise.all(
        activeBranches.map(async (branch) => {
          const { url } = await apiClient.getRevisionDownloadUrl(
            drawingId,
            branch.headRevisionId!
          );
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Failed to fetch revision for branch ${branch.name}`);
          const blob = await response.blob();
          dwgEntries.push({ fileNameWithExtension: `${branch.name}.dwg`, blob });
        })
      );

      const drawingImages = await apiClient.fetchDrawingImages({ drawingId });
      if (drawingImages.length > 0) {
        loadingToastId.close();
        loadingToastId = showLoading(
          t("dashboard:drawings.download.toast.downloadingImages"),
          undefined,
          ALLOW_ON_CANVAS
        );
      }

      const imageEntries: { fileNameWithExtension: string; blob: Blob }[] = [];
      await Promise.all(
        drawingImages.map(async (image) => {
          const { url } = await apiClient.getFileUrl(image.fileId);
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Failed to fetch image`);
          const blob = await response.blob();
          const filename =
            image.rasterPath.split("/").pop() ||
            `image${getExtensionFromBlob(blob)}`;
          imageEntries.push({ fileNameWithExtension: filename, blob });
        })
      );

      await triggerDownloadZip(fileName, [...dwgEntries, ...imageEntries]);
      showSuccess(
        t("dashboard:drawings.download.toast.success"),
        undefined,
        ALLOW_ON_CANVAS
      );
    } catch (error) {
      Logger.error(`useDownloadAllBranches : ${error}`);
      showDanger(
        t("dashboard:drawings.download.toast.error"),
        undefined,
        ALLOW_ON_CANVAS
      );
    } finally {
      loadingToastId?.close();
      isLoading.value = false;
    }
  }

  return { mutateAsync, isLoading };
};

export const useDownloadDwg = () => {
  const { showLoading, showDanger, showSuccess } = useToast();
  const { t } = useTranslation();
  const isLoading = ref(false);

  async function mutateAsync(
    fileName: string,
    drawingId: number,
    revisionId: number
  ) {
    let loadingToastId = showLoading(
      t("dashboard:drawings.download.toast.converting"),
      undefined,
      ALLOW_ON_CANVAS
    );
    isLoading.value = true;
    try {
      const { url } = await apiClient.getRevisionDownloadUrl(
        drawingId,
        revisionId
      );
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch from presigned URL`);
      }
      const dwgBlob = await response.blob();

      loadingToastId.close();
      loadingToastId = showLoading(
        t("dashboard:drawings.download.toast.downloadingDwg"),
        undefined,
        ALLOW_ON_CANVAS
      );

      const fileNameWithExtension = fileName.endsWith(".dwg")
        ? fileName
        : `${fileName}.dwg`;
      const fileNameWithoutExtension = fileName.replace(/\.dwg$/i, "");

      const drawingImages = await apiClient.fetchDrawingImages({ drawingId });
      if (drawingImages.length > 0) {
        loadingToastId.close();
        loadingToastId = showLoading(
          t("dashboard:drawings.download.toast.downloadingImages"),
          undefined,
          ALLOW_ON_CANVAS
        );
      }
      const imageEntries: { filename: string; blob: Blob }[] = [];
      if (drawingImages.length > 0) {
        await Promise.all(
          drawingImages.map(async (image) => {
            const { url } = await apiClient.getFileUrl(image.fileId);
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch image from URL: ${url}`);
            }
            const blob = await response.blob();
            const filename =
              image.rasterPath.split("/").pop() ||
              `image${getExtensionFromBlob(blob)}`;
            imageEntries.push({ filename, blob });
          })
        );
      }

      if (imageEntries.length) {
        triggerDownloadZip(fileNameWithoutExtension, [
          {
            fileNameWithExtension: fileNameWithExtension,
            blob: dwgBlob,
          },
          ...imageEntries.map(({ filename, blob }) => ({
            fileNameWithExtension: filename,
            blob,
          })),
        ]);
      } else {
        triggerDownload(fileNameWithExtension, dwgBlob);
      }

      showSuccess(
        t("dashboard:drawings.download.toast.success"),
        undefined,
        ALLOW_ON_CANVAS
      );
    } catch (error) {
      Logger.error(`downloadFileToUser : ${error}`);
      showDanger(
        t("dashboard:drawings.download.toast.error"),
        undefined,
        ALLOW_ON_CANVAS
      );
    } finally {
      loadingToastId?.close();
      isLoading.value = false;
    }
  }

  return { mutateAsync, isLoading };
};

function getExtensionFromBlob(blob: Blob): string {
  const mimeToExt: Record<string, string> = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/webp": ".webp",
    "image/gif": ".gif",
    "image/bmp": ".bmp",
    "image/tiff": ".tiff",
  };
  return mimeToExt[blob.type] || ".png";
}

// UTILS

export function downloadArrayBuffer(
  binary: ArrayBuffer,
  fileName: string,
  extension: "vsfx" | "dwg"
) {
  const blob = new Blob([new Uint8Array(binary)], {
    type: "application/" + extension,
  });

  // triggerDownload(`${fileName}.${extension}`, URL.createObjectURL(blob));
  triggerDownload(fileName, blob);
}

export function triggerDownload(fileNameWithExtension: string, blob: Blob) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.download = fileNameWithExtension;
  anchor.href = href;

  document.body.append(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);
}

export async function triggerDownloadZip(
  name: string,
  files: { fileNameWithExtension: string; blob: Blob }[]
) {
  const zip = new JSZip();
  files.forEach(({ fileNameWithExtension, blob }) => {
    zip.file(fileNameWithExtension, blob);
  });
  const zipBlob = await zip.generateAsync({ type: "blob" });
  triggerDownload(name + ".zip", zipBlob);
}

/**
 * Trigger database save and download ArrayBuffer straightaway.
 */

export async function downloadDatabase(filename: string) {
  await Facade.file.download(filename);
}
